import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useLang } from "../../i18n/LangContext";

/**
 * Global background music player.
 * - Loops `/audio/studygri.mp3` at ~40% volume.
 * - Playback is unlocked by a user gesture (Intro "Enter" click OR any first click/keydown).
 * - Mute/unmute state persisted in localStorage.
 * - Floating pill button bottom-right with animated equalizer bars.
 * - First-visit tooltip shown for 3 seconds.
 */

const STORAGE_KEY = "sg_music_muted";
const TOOLTIP_SEEN_KEY = "sg_music_tip_seen";
const AUDIO_SRC = "/audio/studygri.mp3";
const DEFAULT_VOLUME = 0.4;

const TIP_I18N = {
  uk: "Увімкнути фонову музику",
  ru: "Включить фоновую музыку",
  en: "Turn on background music",
  pl: "Włącz muzykę w tle",
  it: "Attiva la musica di sottofondo",
  es: "Activar música de fondo",
  ar: "تشغيل موسيقى الخلفية",
  zh: "开启背景音乐",
};

const AudioPlayer = () => {
  const { lang } = useLang();
  const audioRef = useRef(null);
  const startedRef = useRef(false);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [playing, setPlaying] = useState(false);
  const [showTip, setShowTip] = useState(false);

  // Try to (re)start playback respecting mute state
  const ensurePlayback = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = DEFAULT_VOLUME;
    a.muted = muted;
    if (!muted) {
      a.play()
        .then(() => {
          startedRef.current = true;
          setPlaying(true);
        })
        .catch(() => {
          // Autoplay still blocked — will retry on next gesture
        });
    }
  };

  // Listen for intro "Enter" event and any first user gesture as fallback
  useEffect(() => {
    const onIntroDone = () => ensurePlayback();
    const onFirstGesture = () => {
      if (!startedRef.current) ensurePlayback();
    };
    window.addEventListener("sg-audio-start", onIntroDone);
    window.addEventListener("click", onFirstGesture, { once: false });
    window.addEventListener("keydown", onFirstGesture, { once: false });
    window.addEventListener("touchstart", onFirstGesture, { once: false, passive: true });
    return () => {
      window.removeEventListener("sg-audio-start", onIntroDone);
      window.removeEventListener("click", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
  }, [muted]);

  // React to mute/unmute changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = muted;
    try {
      localStorage.setItem(STORAGE_KEY, muted ? "1" : "0");
    } catch (_e) { /* ignore */ }
    if (!muted) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      setPlaying(false);
    }
  }, [muted]);

  // First-visit tooltip: show for 3s after intro dismiss
  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(TOOLTIP_SEEN_KEY) === "1";
    } catch (_e) { /* ignore */ }
    if (seen) return;
    const openTimer = setTimeout(() => setShowTip(true), 900);
    const closeTimer = setTimeout(() => {
      setShowTip(false);
      try {
        localStorage.setItem(TOOLTIP_SEEN_KEY, "1");
      } catch (_e) { /* ignore */ }
    }, 3900);
    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  const toggleMute = () => setMuted((m) => !m);

  const tipText = TIP_I18N[lang] || TIP_I18N.en;

  return (
    <>
      <audio ref={audioRef} src={AUDIO_SRC} loop preload="auto" />
      <div className="fixed bottom-5 right-5 z-[9997] flex items-end gap-2 pointer-events-none">
        {showTip && (
          <div
            className="pointer-events-none px-3 py-2 rounded-full text-xs font-medium shadow-lg animate-in fade-in slide-in-from-right-2"
            style={{
              background: "rgba(11,20,48,0.95)",
              color: "#E8D5AB",
              border: "1px solid rgba(201,169,110,0.35)",
              whiteSpace: "nowrap",
              backdropFilter: "blur(6px)",
            }}
            data-testid="audio-tooltip"
          >
            {tipText}
          </div>
        )}
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? tipText : "Mute background music"}
          data-testid="audio-toggle-btn"
          className="pointer-events-auto group relative w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 hover:scale-110"
          style={{
            background: muted
              ? "rgba(11,20,48,0.9)"
              : "linear-gradient(135deg, #C9A96E 0%, #E8D5AB 100%)",
            border: "1px solid rgba(201,169,110,0.6)",
            backdropFilter: "blur(6px)",
          }}
        >
          {muted ? (
            <VolumeX className="w-5 h-5" style={{ color: "#C9A96E" }} />
          ) : (
            <div className="flex items-end gap-[3px] h-4">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="w-[3px] rounded-full"
                  style={{
                    background: "#0B1430",
                    height: "40%",
                    animation: playing
                      ? `sg-eq 900ms ease-in-out ${i * 120}ms infinite`
                      : "none",
                  }}
                />
              ))}
            </div>
          )}
          {/* Fallback icon (accessibility) */}
          {!muted && (
            <Volume2 className="w-4 h-4 absolute opacity-0" aria-hidden="true" />
          )}
        </button>
      </div>
      <style>{`
        @keyframes sg-eq {
          0%, 100% { height: 25%; }
          50% { height: 95%; }
        }
      `}</style>
    </>
  );
};

export default AudioPlayer;
